import { QueryKey, useQuery as useReactQuery } from '@tanstack/react-query'
import { AxiosError, AxiosResponse } from 'axios'
import { useState } from 'react'

import { isObject } from 'lodash'
import { APIErrorHandler } from '../APIErrorHandler/APIErrorHandler'
import { PBPGetResponse, QueryOptions, QueryReturn } from './useQuery.types'

export const useQuery = <
  A,
  D,
  B extends D | PBPGetResponse<D> = PBPGetResponse<D>,
  TOptions extends QueryOptions<D> = QueryOptions<D>
>(
  queryKey: QueryKey,
  query: (_: A) => Promise<AxiosResponse<B>>,
  queryProps: A,
  options?: TOptions
): QueryReturn<D, B, TOptions> => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const queryFn = async () => {
    try {
      const res = await query(queryProps)

      if (res.status === 204 || !res.data) {
        setErrorMessage('No results')
      } else {
        setErrorMessage(null)
      }

      if (!options?.shallowData && isObject(res.data) && 'data' in res.data)
        return (res.data as B extends PBPGetResponse<D> ? B : never).data

      return res.data as D
    } catch (_err) {
      const err = _err as AxiosError<{ error: Record<string, unknown> }>

      setErrorMessage(APIErrorHandler(err?.response?.data?.error))
      throw err
    }
  }

  const {
    data,
    isLoading,
    error,
    refetch,
    status,
    isPending,
    isFetching,
    isPlaceholderData,
  } = useReactQuery({
    ...options,
    queryFn,
    queryKey,
  })

  return {
    data: data ?? null,
    isLoading,
    errorMessage,
    error,
    status,
    refetch,
    isPending,
    isFetching,
    isPlaceholderData,
  } as QueryReturn<D, B, TOptions>
}
